import {Button, Card, CardContent, CardHeader, Typography} from "@mui/material";
import React, {FC, useCallback} from "react";
import {Log} from "../../../types/models/Log";
import {useNavigate} from "react-router";
import {Add, Remove, SwitchLeft} from "@mui/icons-material";
import TimberTypeName from "../../timberType/TimberTypeDetail/TimberTypeName.tsx";
import TimberClassName from "../../timberClass/TimberClassDetail/TimberClassName.tsx";
import ThicknessClassName from "../../thicknessClass/ThicknessClassDetail/ThicknessClassName.tsx";
import ShipmentName from "../ShipmentDetail/ShipmentName.tsx";
import {PrimaryShipment, Shipment} from "../../../types/models/Shipment.ts";
import {useStateSession} from "../../../../hooks/useAuth.ts";
import {useGetCuttingQuery} from "../../../apis/Cutting.api.ts";
import {PrimaryCutting} from "../../../types/models/Cutting.ts";
import {useGetShipmentQuery} from "../../../apis/Shipment.api.ts";
import {cuttingStateEditCheck, shippingStateCheck, shippingStateEditCheck} from "../../../stateChecks.ts";
import TimberStorageName from "../../timberStorage/TimberStorageDetail/TimberStorageName.tsx";
import {fixedFormat} from "../../../../utils/numberFormat.ts";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";

type IFormSelectProps = {
    shipment: Shipment,
    data: Log;
    onAddLog?: (log: Log) => void;
    onRemoveLog?: (log: Log) => void;
}


const LogDetail: FC<IFormSelectProps> = ({data, onAddLog, onRemoveLog, shipment}) => {

    const navigate = useNavigate()
    const session = useStateSession()

    const onAddLogCallback = useCallback((e: any, log: Log) => {
        e.stopPropagation()
        onAddLog(log)
    }, [navigate])

    const onRemoveLogCallback = useCallback((e: any, log: Log) => {
        e.stopPropagation()
        onRemoveLog(log)
    }, [navigate])

    const isInShipment = data.shipmentId === shipment.id
    const isNotInAnyShipment = data.shipmentId == undefined
    const isInOtherShipment = !isInShipment && !isNotInAnyShipment
    const isInOtherStorage = data.storageId !== shipment.storageId

    const { data: cutting } = useGetCuttingQuery(PrimaryCutting.fromId(shipment?.cuttingId), {skip: !shipment?.cuttingId})
    const { data: logShipment } = useGetShipmentQuery(PrimaryShipment.fromId(data?.shipmentId), {skip: isInShipment || !data?.shipmentId})
    
    const showEdit = session.hasPermission('', 'applications/timber/entities/Shipments/update/view')
        && cuttingStateEditCheck(cutting?.state)
        && shippingStateEditCheck(shipment?.state)
        && (!logShipment || shippingStateEditCheck(logShipment?.state))
        && shippingStateCheck(session, shipment?.state)
    
    const color = isInShipment ? 'red' : isInOtherShipment ? 'orange' : 'blue'

    return (
        <Card variant="outlined">
            <CardHeader
                title={data.internalKey}
                action={isInOtherStorage ?
                    <>
                        <Trans>Storage</Trans>: <TimberStorageName id={data.storageId}/>
                    </>
                    :
                    <>
                    {isNotInAnyShipment && onAddLog && showEdit && (
                        <Button size="small" variant="outlined" startIcon={<Add/>} onClick={(e) => onAddLogCallback(e, data)}>
                            <Trans>Add to shipment</Trans>
                        </Button>)}
                    {isInOtherShipment && onAddLog && showEdit && (
                        <Button size="small" variant="outlined" color="warning" startIcon={<SwitchLeft/>}
                                onClick={(e) => onAddLogCallback(e, data)}>
                            <Trans>Move to this shipment</Trans>
                        </Button>)}
                    {isInShipment && onRemoveLog && showEdit && (
                        <Button size="small" variant="outlined" color="error" startIcon={<Remove/>}
                                onClick={(e) => onRemoveLogCallback(e, data)}>
                            <Trans>Remove from this shipment</Trans>
                        </Button>)}
                    </>
            }>
            </CardHeader>
            <CardContent>
                {data.shipmentId && (
                    <Typography variant="body2" textAlign="left" sx={{color: color}}>
                        <Trans>Shipment</Trans>: <ShipmentName  id={data.shipmentId} />
                    </Typography>
                )}
                <Typography variant="body2" textAlign="left">
                    <Trans>Diameter</Trans>: {data.diameter}
                </Typography>
                <Typography variant="body2" textAlign="left">
                    <Trans>Bark Thickness</Trans>: {data.barkThickness}
                </Typography>
                <Typography variant="body2" textAlign="left">
                    <Trans>Length</Trans>: {data.length}
                </Typography>
                <Typography variant="body2" textAlign="left">
                    <Trans>Volume</Trans>: {fixedFormat(data.volume)}
                </Typography>
                <Typography variant="body2" textAlign="left">
                    <Trans>Timber Type</Trans>: <TimberTypeName id={data.timberTypeId} />
                </Typography>
                <Typography variant="body2" textAlign="left">
                    <Trans>Class</Trans>: <TimberClassName id={data.classId} />
                </Typography>
                <Typography variant="body2" textAlign="left">
                    <Trans>Thickness Class</Trans>: <ThicknessClassName id={data.thicknessClassId} />
                </Typography>
                <Typography variant="body2" textAlign="left">
                    <Trans>Storage</Trans>: <TimberStorageName id={data.storageId}/>
                </Typography>
                {data.description ?
                    <Typography variant="body2" textAlign="left">
                        <Trans>Description</Trans>: {data.description}
                    </Typography>
                    :
                    <></>
                }
            </CardContent>
        </Card>
    )
}
export default LogDetail;
